import { useCountry } from "@/OneLiners";
import { CountryModel } from "../Models/CountryModel";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";

/**
 * =====================================================
 * Hook: useCountryEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich Country domain data with related users.
 * Combines user information into CountryModel for detailed views.
 *
 * Responsibilities:
 * - Fetch and maintain related data (users)
 * - Track combined loading/error state
 * - Provide helper functions to enrich a single country, by ID, or a list
 *
 * Notes:
 * - Returns a pure API for enrichment; no side effects beyond data fetching.
 * - Compatible with detail and list views for cities.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export function useCountryEnricher() {

    /**
             * Centralized audit enrichment hook
             * - Provides O(1) user resolution via precomputed Map
             * - Exposes audit enrichment helpers and status/error
             */
    const {
        enricherResponse: auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<CountryModel>();

    const { dataMap, stateResponse } = useCountry();


    /** =====================================================
     * Combined Loading & Error Status
     * =====================================================
     *
     * Combines status from all related hooks into a single ActionResponse:
     * - status: combined loading state (idle, loading, success, failed)
     * - error: first non-null error encountered
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(stateResponse.status, auditEnricherResponse.status),
        error: stateResponse.error ?? auditEnricherResponse.error
    };

    /** =====================================================
     * Public Helper Functions
     * =====================================================
     *
     * These functions enrich cities with:
     * - Audit info (created/updated by, timestamps)
     * - Related users (creator/updater)
     */


    // Enrich a list of cities
    const getEnrichedCountryMap = ( countryMap: RecordMap<CountryModel>): RecordMap<CountryModel> => {
        return countryMap.map<CountryModel>(getStrictEnrichedCountry);
    }

    const getStrictEnrichedCountry = (country: CountryModel): CountryModel => {
        return getEnrichedAudit(country);

    }

    // Enrich a single country with audit/user info
    const getEnrichedCountry = (country?: CountryModel): CountryModel | undefined => {
        if (!country) return undefined;
        return getStrictEnrichedCountry(country);
    }

    const getEnrichedCountryById = (id?: number | null): CountryModel | undefined => {
        if (!id) return undefined;
        return getEnrichedCountry(dataMap.get(id));
    }

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Provides all enriched data and helper functions
     * for consuming components.
     */
    return {
        countryMap: dataMap,
        response,
        getEnrichedCountry,
        getEnrichedCountryById,
        getEnrichedCountryMap
    }
}